// src/review/ReviewScreen.tsx: /review#t=<token>, the page homebuild's owner opens from "Cedar Row Builders is live" (docs/DECISIONS.md
// "No setup fee; builders go live on their own (owner, 2026-10-20)"; docs/SPEC.md 17). It renders on app.homebuild.ai, where there
// is no builder, so src/main.tsx shows it before the normal boot: homebuild's ink, the app's type and buttons. Opening it only peeks
// (POST /api/platform/review/peek: mail scanners open links); Pause (after its inline confirm) and Resume are the two POSTs that
// change anything. The link works for 90 days and as often as it is opened. The token is read from the fragment and the fragment is
// cleared from the address bar at once. A link that is not valid shows the one plain message and nothing else.
import { useEffect, useState } from "preact/hooks";
import type { BuilderReview } from "../../shared/api";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { Button, Icon, Pill, Spinner } from "../ui";
import { isPhone } from "../ui/media";
import "../styles/review.css";

const HB_INK_LOCKUP = "/brand/homebuild-ink.svg";
export const INVALID_LINK = "This review link isn't valid any more.";

/** The token in the fragment (`#t=…`), or null. */
export function tokenFromHash(hash: string): string | null {
  const t = new URLSearchParams(hash.replace(/^#/, "")).get("t");
  return t && t.length <= 200 ? t : null;
}

/** Reads the token and takes it out of the address bar, so it stays in no history entry, screenshot or shared URL. */
function takeToken(): string | null {
  const token = tokenFromHash(window.location.hash);
  if (window.location.hash) {
    try {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch {
      // The address bar keeps it; the page still works.
    }
  }
  return token;
}

function zoned(ms: number, tz: string, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(ms);
  } catch {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "America/New_York" }).format(ms);
  }
}

/** "Went live today at 2:14 PM · Saratoga Springs, NY", or "Went live Oct 20 at 2:14 PM" on another day, in the builder's time zone. */
export function liveLine(liveAt: string | null, tz: string, office: string, now: number = Date.now()): string {
  if (!liveAt) return office;
  const ms = Date.parse(liveAt);
  const day = (t: number) => zoned(t, tz, { year: "numeric", month: "numeric", day: "numeric" });
  const time = zoned(ms, tz, { hour: "numeric", minute: "2-digit" });
  const when = day(ms) === day(now) ? `today at ${time}` : `${zoned(ms, tz, { month: "short", day: "numeric" })} at ${time}`;
  return `Went live ${when}${office ? ` · ${office}` : ""}`;
}

/** What Pause will do, in the owner's words. A builder that was refunded or whose service ended has no plan billing. */
export function pauseWords(company: string, service?: BuilderReview["service"]): string {
  const offline = `${company} goes offline. Families can't start or open boards; the team can still sign in.`;
  if (service?.state === "refunded" || service?.state === "ended") return `${offline} Their plan has ended, so nothing is billing.`;
  return `${offline} Their plan keeps billing until you or they cancel it.`;
}

const SERVICE_PILL: Readonly<Record<NonNullable<BuilderReview["service"]>["state"], string>> = { active: "Active", refunded: "Refunded", ended: "Ended" };

/** What Resume did. */
export function resumedWords(company: string): string {
  return `${company} is live again.`;
}

type View =
  | { kind: "loading" }
  | { kind: "ready"; review: BuilderReview }
  | { kind: "invalid" }
  | { kind: "error" };

function Header() {
  return (
    <header class="rv-top">
      <div class="rv-top-in">
        <img class="rv-hb" src={HB_INK_LOCKUP} alt="homebuild" />
        <span class="rv-for">Builders</span>
      </div>
    </header>
  );
}

function Frame(props: { children: preact.ComponentChildren; bar?: preact.ComponentChildren }) {
  return (
    <div class="rv" data-screen="review">
      <Header />
      <main class="rv-main" id="main" tabIndex={-1}>
        {props.children}
      </main>
      {props.bar}
    </div>
  );
}

function Confirm(props: { words: string; busy: boolean; problem: string | null; onPause: () => void; onCancel: () => void }) {
  return (
    <div class="rv-confirm" role="group" aria-label="Pause">
      <p>{props.words}</p>
      {props.problem ? (
        <p class="rv-fail" role="alert">
          <Icon name="info" size="sm" />
          {props.problem}
        </p>
      ) : null}
      <div class="rv-confirm-act">
        <Button busy={props.busy} onClick={props.onPause}>
          Pause
        </Button>
        <Button variant="secondary" disabled={props.busy} onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Review(props: { review: BuilderReview; token: string; onChanged: (r: BuilderReview) => void; onInvalid: () => void }) {
  const { review: r } = props;
  const phone = isPhone.value;
  const paused = r.status === "paused";
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /** Pause or Resume: the page then shows the status the Worker answered. */
  const change = async (to: "paused" | "live") => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const done = to === "paused" ? await api.reviewPause(props.token) : await api.reviewResume(props.token);
      setConfirming(false);
      setNote(done.status === "live" ? resumedWords(r.company) : null);
      props.onChanged({ ...r, status: done.status });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) props.onInvalid();
      else setProblem(e instanceof ApiError ? e.message : "That didn't go through. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setConfirming(false);
    setProblem(null);
  };
  const ask = `mailto:${r.contactEmail}?subject=${encodeURIComponent("Your homebuild setup")}`;
  const actions = (
    <>
      {r.contactEmail ? (
        <Button variant="secondary" icon="msg" href={ask}>
          Ask a Question
        </Button>
      ) : null}
      {paused ? (
        <Button busy={busy} icon="play" onClick={() => void change("live")}>
          Resume
        </Button>
      ) : (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Pause
        </Button>
      )}
    </>
  );
  const confirm = <Confirm words={pauseWords(r.company, r.service)} busy={busy} problem={problem} onPause={() => void change("paused")} onCancel={cancel} />;

  const bar = phone ? <div class="rv-mbar">{confirming ? confirm : actions}</div> : undefined;
  return (
    <Frame bar={bar}>
      <div class="rv-col">
        <div class="rv-head">
          <div class="rv-head-t">
            {r.logo ? <img class="rv-logo" src={r.logo.url} alt={r.logo.alt} /> : null}
            <h1 class="h-page">{r.company}</h1>
            <p class="t-meta rv-state">
              <Pill tone={paused ? "line" : "navy"} dot>
                {paused ? "Paused" : "Live"}
              </Pill>
              {r.service ? <Pill tone="line">{SERVICE_PILL[r.service.state]}</Pill> : null}
              <span>{liveLine(r.liveAt, r.timezone, r.office)}</span>
            </p>
          </div>
          {phone ? null : <div class="rv-act">{confirming ? null : actions}</div>}
        </div>
        {!phone && confirming ? confirm : null}
        {note && !paused ? (
          <p class="rv-note" role="status">
            <Icon name="check-c" size="sm" />
            {note}
          </p>
        ) : null}
        {problem && !confirming ? (
          <p class="rv-fail rv-note" role="alert">
            <Icon name="info" size="sm" />
            {problem}
          </p>
        ) : null}

        {r.toCheck.length ? (
          <section class="rv-check" aria-label="To check">
            <h2 class="rv-h2">To check</h2>
            <ul>
              {r.toCheck.map((line) => (
                <li key={line}>
                  <Icon name="flag" size="sm" />
                  {line}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div class="rv-grid">
          <section class="card rv-facts" aria-label="Their details">
            <dl class="rv-pairs">
              {r.facts.map((f) => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>
                    {f.value}
                    {f.sub ? <span class="rv-sub">{f.sub}</span> : null}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
          <div class="rv-lists">
            <section aria-label="Homes">
              <h2 class="rv-h2">Homes</h2>
              {r.homes.length ? (
                <ul class="rv-homes">
                  {r.homes.map((h, i) => (
                    <li key={`${h.name}${i}`}>
                      {h.photo ? <img class="rv-thumb" src={h.photo} alt="" loading="lazy" /> : <span class="rv-thumb rv-thumb-none" aria-label="No photo" />}
                      <span class="rv-li-t">
                        <b>{h.name}</b>
                        <span class="rv-sp">
                          {h.style ? <span>{h.style}</span> : null}
                          {h.specs ? <span>{h.specs}</span> : null}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p class="rv-none">None yet</p>
              )}
            </section>
            <section aria-label="Communities">
              <h2 class="rv-h2">Communities</h2>
              {r.communities.length ? (
                <ul class="rv-comms">
                  {r.communities.map((c, i) => (
                    <li key={`${c.name}${i}`}>
                      <span class="rv-li-t">
                        <b>{c.name}</b>
                        <span>{[c.town, c.status].filter(Boolean).join(" · ")}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p class="rv-none">None yet</p>
              )}
            </section>
          </div>
        </div>
      </div>
    </Frame>
  );
}

export function ReviewScreen(props: { token?: string | null }) {
  const [token] = useState<string | null>(() => (props.token === undefined ? takeToken() : props.token));
  const [view, setView] = useState<View>(token ? { kind: "loading" } : { kind: "invalid" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    document.title = "Review · homebuild";
    if (!token) return;
    const ctl = new AbortController();
    api
      .reviewPeek(token, { signal: ctl.signal })
      .then((review) => setView({ kind: "ready", review }))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setView(e instanceof ApiError && e.status === 404 ? { kind: "invalid" } : { kind: "error" });
      });
    return () => ctl.abort();
  }, [token, attempt]);

  switch (view.kind) {
    case "loading":
      return (
        <Frame>
          <Spinner label="Opening the review" />
        </Frame>
      );
    case "ready":
      return <Review review={view.review} token={token!} onChanged={(review) => setView({ kind: "ready", review })} onInvalid={() => setView({ kind: "invalid" })} />;
    case "invalid":
      return (
        <Frame>
          <div class="rv-col rv-narrow">
            <h1 class="h-page">{INVALID_LINK}</h1>
          </div>
        </Frame>
      );
    case "error":
      return (
        <Frame>
          <div class="rv-col rv-narrow">
            <h1 class="h-page">We couldn't load this page</h1>
            <p class="t-lede">Check your connection and try again.</p>
            <div class="hb-actions">
              <Button
                onClick={() => {
                  setView({ kind: "loading" });
                  setAttempt((n) => n + 1);
                }}
              >
                Try Again
              </Button>
            </div>
          </div>
        </Frame>
      );
  }
}
